import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { PenTool, Check, X, Loader2, AlertTriangle } from "lucide-react";

// Component to display existing signatures
export function FirmasDisplay({ firmas }) {
  if (!firmas?.firma_solicitante && !firmas?.firma_supervisor) {
    return null;
  }

  return (
    <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg border">
      <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
        <PenTool className="w-4 h-4" />
        Firmas de la Solicitud
      </h4>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Employee Signature */}
        {firmas.firma_solicitante && (
          <div className="p-3 bg-white dark:bg-slate-700 rounded border">
            <p className="text-xs text-slate-500 mb-1">Firma del Solicitante</p>
            <div className="flex items-center gap-3">
              {firmas.firma_solicitante.firma_url && (
                <img 
                  src={firmas.firma_solicitante.firma_url} 
                  alt="Firma solicitante"
                  className="h-12 object-contain border rounded bg-white"
                />
              )}
              <div>
                <p className="text-sm font-medium">{firmas.firma_solicitante.user_name}</p>
                <p className="text-xs text-slate-500">
                  {new Date(firmas.firma_solicitante.fecha_firma).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Supervisor Signature */}
        {firmas.firma_supervisor && (
          <div className={`p-3 rounded border ${
            firmas.firma_supervisor.accion === "aprobar" 
              ? "bg-green-50 dark:bg-green-900/20 border-green-200" 
              : "bg-red-50 dark:bg-red-900/20 border-red-200"
          }`}>
            <p className="text-xs text-slate-500 mb-1">
              Firma del Supervisor ({firmas.firma_supervisor.accion === "aprobar" ? "Aprobación" : "Rechazo"})
            </p>
            <div className="flex items-center gap-3">
              {firmas.firma_supervisor.firma_url && (
                <img 
                  src={firmas.firma_supervisor.firma_url} 
                  alt="Firma supervisor"
                  className="h-12 object-contain border rounded bg-white"
                />
              )}
              <div>
                <p className="text-sm font-medium">{firmas.firma_supervisor.user_name}</p>
                <p className="text-xs text-slate-500">
                  {new Date(firmas.firma_supervisor.fecha_firma).toLocaleString()}
                </p>
                {firmas.firma_supervisor.comentario && (
                  <p className="text-xs text-slate-600 mt-1 italic">
                    "{firmas.firma_supervisor.comentario}"
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Button to sign a request (for employees)
export function FirmarSolicitudButton({ 
  solicitudId, 
  tipoSolicitud, 
  onFirmado,
  disabled = false,
  className = ""
}) {
  const [loading, setLoading] = useState(false);
  const [hasFirma, setHasFirma] = useState(false);
  const { getAuthHeaders, user } = useAuth();

  useEffect(() => {
    // Check if user has a signature configured
    const checkFirma = async () => {
      try {
        const res = await axios.get(`${API}/firma-digital/mi-firma`, getAuthHeaders());
        setHasFirma(!!res.data?.firma_url);
      } catch (err) {
        setHasFirma(false);
      }
    };
    checkFirma();
  }, [getAuthHeaders]);

  const handleFirmar = async () => {
    if (!hasFirma) {
      toast.error("No tiene una firma configurada. Vaya a Configuración > Firma Digital para configurarla.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/firma-digital/firmar-solicitud`, {
        solicitud_id: solicitudId,
        tipo_solicitud: tipoSolicitud,
        usar_firma_guardada: true
      }, getAuthHeaders());
      
      toast.success("Solicitud firmada correctamente");
      if (onFirmado) onFirmado();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al firmar");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleFirmar}
      disabled={disabled || loading}
      className={className}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
      ) : (
        <PenTool className="w-4 h-4 mr-2" />
      )}
      Firmar
    </Button>
  );
}

// Dialog for supervisor to approve/reject with signature
export function AprobarConFirmaDialog({
  open,
  onOpenChange,
  solicitud,
  tipoSolicitud,
  onComplete
}) {
  const [loading, setLoading] = useState(false);
  const [comentario, setComentario] = useState("");
  const [hasFirma, setHasFirma] = useState(false);
  const [checkingFirma, setCheckingFirma] = useState(true);
  const { getAuthHeaders } = useAuth();

  // Check if this is a status change (not a new approval)
  const isStatusChange = solicitud?.estado && solicitud.estado !== "pendiente";
  const estadoActual = solicitud?.estado || "pendiente";

  useEffect(() => {
    if (open) {
      // Check if supervisor has a signature configured
      const checkFirma = async () => {
        setCheckingFirma(true);
        try {
          const res = await axios.get(`${API}/firma-digital/mi-firma`, getAuthHeaders());
          setHasFirma(!!res.data?.firma_url);
        } catch (err) {
          setHasFirma(false);
        } finally {
          setCheckingFirma(false);
        }
      };
      checkFirma();
      setComentario("");
    }
  }, [open, getAuthHeaders]);

  const handleAction = async (accion) => {
    if (!hasFirma) {
      toast.error("Debe configurar su firma digital antes de aprobar/rechazar solicitudes. Vaya a Configuración > Firma Digital.");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("solicitud_id", solicitud.id);
      formData.append("tipo_solicitud", tipoSolicitud);
      formData.append("accion", accion);
      // Include previous state for balance adjustment
      formData.append("estado_anterior", estadoActual);
      if (comentario) {
        formData.append("comentario", comentario);
      }

      await axios.post(`${API}/firma-digital/firmar-aprobacion`, formData, getAuthHeaders());
      
      const nuevoEstado = accion === "aprobar" ? "aprobada" : accion === "rechazar" ? "rechazada" : "pendiente";
      toast.success(
        isStatusChange 
          ? `Estado cambiado de "${estadoActual}" a "${nuevoEstado}" correctamente` 
          : `Solicitud ${nuevoEstado} y firmada correctamente`
      );
      onOpenChange(false);
      if (onComplete) onComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar la solicitud");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md dark:bg-slate-800">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenTool className="w-5 h-5 text-blue-600" />
            {isStatusChange ? "Cambiar Estado de Solicitud" : "Aprobar/Rechazar Solicitud"}
          </DialogTitle>
          <DialogDescription>
            {isStatusChange 
              ? "Puede cambiar el estado de esta solicitud. Los saldos se ajustarán automáticamente."
              : "Su firma digital será registrada con esta acción"
            }
          </DialogDescription>
        </DialogHeader>

        {/* Show current status warning for status changes */}
        {isStatusChange && (
          <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                  Estado actual: <span className={`px-2 py-0.5 rounded text-xs ${
                    estadoActual === "aprobada" ? "bg-green-100 text-green-700" : 
                    estadoActual === "rechazada" ? "bg-red-100 text-red-700" : 
                    "bg-amber-100 text-amber-700"
                  }`}>{estadoActual.toUpperCase()}</span>
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  {estadoActual === "aprobada" 
                    ? "Si cambia a rechazada, se devolverán los días al saldo."
                    : "Si cambia a aprobada, se descontarán los días del saldo."}
                </p>
              </div>
            </div>
          </div>
        )}

        {checkingFirma ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : !hasFirma ? (
          <div className="py-4">
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-300">Firma no configurada</p>
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                  Debe configurar su firma digital antes de aprobar o rechazar solicitudes.
                  Vaya a <strong>Configuración → Firma Digital</strong> para cargar su firma.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">Firma digital configurada</span>
              </div>
            </div>

            <div>
              <Label>Comentario {isStatusChange ? "(motivo del cambio)" : "(opcional)"}</Label>
              <Textarea
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder={isStatusChange ? "Explique el motivo del cambio de estado..." : "Agregue un comentario si lo desea..."}
                className="mt-1"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          {hasFirma && !checkingFirma && (
            <>
              {/* Show Revert to Pending button only for status changes */}
              {isStatusChange && (
                <Button
                  variant="outline"
                  className="border-amber-500 text-amber-600 hover:bg-amber-50"
                  onClick={() => handleAction("pendiente")}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Volver a Pendiente
                </Button>
              )}
              {/* Hide reject button if already rejected */}
              {estadoActual !== "rechazada" && (
                <Button
                  variant="destructive"
                  onClick={() => handleAction("rechazar")}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
                  Rechazar
                </Button>
              )}
              {/* Hide approve button if already approved */}
              {estadoActual !== "aprobada" && (
                <Button
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => handleAction("aprobar")}
                  disabled={loading}
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                  Aprobar
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default { FirmasDisplay, FirmarSolicitudButton, AprobarConFirmaDialog };
